import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Card,
  CardContent,
  CardFooter,
  CardHeader,
} from '@repo/shared-client/components/ui'
import { createFileRoute, Link, useNavigate, useSearch } from '@tanstack/react-router'
import LogoFull from '@web/src/components/primatives/logo-full/logo-full'
import { env } from '@web/src/lib/env.client'
import { useTranslation } from '@web/src/lib/intl/react'
import { robotsMetaForEnvironment, seo } from '@web/src/lib/seo'
import { CheckCircleIcon } from 'lucide-react'

export const Route = createFileRoute('/(auth)/(fullscreen-layout)/auth/email-verified')({
  staticData: {
    disableSeasonalTheme: true,
  },
  component: EmailVerified,
  validateSearch: (search: Record<string, unknown>) => ({
    error: (search.error as string) || undefined,
  }),
  head: () =>
    seo({
      title: `Email Verified | ${env.VITE_APP_NAME}`,
      description: 'Email verified',
      robots: robotsMetaForEnvironment({
        production: 'index, follow',
        staging: 'noindex,nofollow',
      }),
    }),
})

function EmailVerified() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { error: errorToken } = useSearch({ strict: false })

  if (errorToken) {
    return (
      <div className="relative z-10 flex min-h-screen items-center justify-center">
        <Card className="mx-4 w-full max-w-md shadow-2xl">
          <CardHeader className="justify-center">
            <LogoFull size="md" />
          </CardHeader>

          <CardContent>
            <Alert variant="destructive">
              <AlertTitle>{t('Email Verification Failed')}</AlertTitle>
              <AlertDescription>
                {t(
                  'The email verification link is no longer valid or expired. Please request a new email verification if you would like to verify your email address.'
                )}
              </AlertDescription>
            </Alert>
          </CardContent>

          <CardFooter className="justify-center">
            <Link className="font-medium text-primary text-sm" to="/auth/login">
              {t('Back to Sign In')}
            </Link>
          </CardFooter>
        </Card>
      </div>
    )
  }

  return (
    <div className="relative z-10 flex min-h-screen items-center justify-center">
      <Card className="mx-4 w-full max-w-md shadow-2xl">
        <CardHeader className="justify-center pb-0">
          <LogoFull size="md" />
        </CardHeader>

        <CardContent className="flex flex-col gap-4 py-4">
          <CheckCircleIcon aria-hidden className="mx-auto size-20 text-green-500" strokeWidth={2} />
          <div className="flex flex-col gap-2 text-center">
            <h1 className="font-semibold text-2xl text-foreground">{t('Email Verified')}</h1>
            <p className="mx-auto max-w-sm text-base text-muted-foreground">
              {t('Your email has been successfully verified. You can now access all features of your account.')}
            </p>
          </div>
        </CardContent>

        <CardFooter className="justify-center">
          <Button className="w-full" onClick={() => navigate({ href: '/auth/welcome' })}>
            {t('Continue')}
          </Button>
        </CardFooter>
      </Card>
    </div>
  )
}
